import { type ColDef, type GetRowIdParams } from 'ag-grid-community';
import { type CustomCellRendererProps } from 'ag-grid-react';
import moment from 'moment';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import { GroupsIcon } from '@treams/treams-icons';
import { Card, DataGrid, Empty, Heading3, Label, Select, UserRow } from '@treams/treams-ui';

import {
	RETENTION_RISKS,
	type RetentionRiskLevel,
	type RetentionRiskPerson,
	retentionRiskLabelKey,
} from '../../talent-reviews/v2/retentionRisk';

const UserLink = styled(Link)`
	text-decoration: none;
	color: inherit;
	min-width: 0;
`;

const RetentionGrid = styled.div`
	min-width: 0;

	&& .ag-layout-auto-height .ag-center-cols-viewport,
	&& .ag-layout-auto-height .ag-center-cols-container {
		min-height: 0;
	}
`;

/**
 * Retention risk under succession: one dropdown option per risk, then the
 * people who currently have that risk.
 * @param props component props
 * @param props.rows everyone with a risk in the current analytics filters
 */
export function TalentReviewRetentionRiskCard(props: { rows: RetentionRiskPerson[] }) {
	const { t } = useTranslation();
	const [risk, setRisk] = useState<RetentionRiskLevel | undefined>('HIGH');

	const options = useMemo(
		() =>
			RETENTION_RISKS.map((value) => ({
				title: t(retentionRiskLabelKey(value)),
				value,
			})),
		[t],
	);

	const rows = useMemo(
		() => (risk ? props.rows.filter((row) => row.retentionRisk === risk) : []),
		[props.rows, risk],
	);

	const columnDefs = useMemo<ColDef<RetentionRiskPerson>[]>(
		() => [
			{
				colId: 'person',
				headerName: t('analytics.talentReview.retentionRisk.column.person'),
				flex: 1.4,
				minWidth: 220,
				sortable: true,
				valueGetter: ({ data }) => (data ? `${data.firstName} ${data.lastName}` : ''),
				cellRenderer: ({ data }: CustomCellRendererProps<RetentionRiskPerson>) =>
					data ? (
						<UserLink to={`/users/${data.userID}`}>
							<UserRow
								user={{
									firstName: data.firstName,
									lastName: data.lastName,
									avatar: data.avatarUrl ? { source: { url: data.avatarUrl } } : null,
								}}
								avatarProps={{ size: 32 }}
							/>
						</UserLink>
					) : null,
			},
			{
				colId: 'department',
				headerName: t('analytics.talentReview.retentionRisk.column.department'),
				flex: 1,
				minWidth: 160,
				sortable: true,
				valueGetter: ({ data }) => data?.department ?? '',
				cellRenderer: ({ data }: CustomCellRendererProps<RetentionRiskPerson>) =>
					data ? <Label>{data.department}</Label> : null,
			},
			{
				colId: 'updatedAt',
				headerName: t('analytics.talentReview.retentionRisk.column.updatedAt'),
				flex: 1,
				minWidth: 160,
				sortable: true,
				valueGetter: ({ data }) => data?.updatedAt.getTime() ?? 0,
				cellRenderer: ({ data }: CustomCellRendererProps<RetentionRiskPerson>) =>
					data ? <Label>{moment(data.updatedAt).format('LL')}</Label> : null,
			},
		],
		[t],
	);

	return (
		<Card>
			<Heading3>{t('analytics.talentReview.retentionRisk.title')}</Heading3>
			<Box mt={2} maxWidth={320}>
				<Select
					small
					role="combobox"
					aria-label={t('analytics.talentReview.retentionRisk.title')}
					placeholder={t('analytics.talentReview.retentionRisk.placeholder')}
					selectedValue={risk}
					options={options}
					onChange={(value) => setRisk(value as RetentionRiskLevel | undefined)}
				/>
			</Box>
			{risk && (
				<Box mt={3} mx={-3} mb={-3}>
					{rows.length === 0 ? (
						<Flex py={5} justifyContent="center">
							<Empty
								small
								icon={<GroupsIcon />}
								title={t('analytics.talentReview.retentionRisk.empty.title')}
								description={t('analytics.talentReview.retentionRisk.empty.description')}
							/>
						</Flex>
					) : (
						<RetentionGrid>
							<DataGrid<RetentionRiskPerson>
								rowData={rows}
								columnDefs={columnDefs}
								getRowId={({ data }: GetRowIdParams<RetentionRiskPerson>) => data.userID}
								domLayout="autoHeight"
							/>
						</RetentionGrid>
					)}
				</Box>
			)}
		</Card>
	);
}

TalentReviewRetentionRiskCard.Skeleton = function TalentReviewRetentionRiskCardSkeleton() {
	return (
		<Card>
			<Heading3.Skeleton width={180} />
			<Box mt={2} maxWidth={320}>
				<Select.Skeleton />
			</Box>
		</Card>
	);
};
